import React, { useState, useEffect, useRef } from 'react';
import { Typography, Table, Button, Modal, Form, Select, InputNumber, DatePicker, Tooltip, message, Popconfirm, Alert, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import _ReportingService from '../../modules/ReportingService';
import _NewapplicationService from '../../modules/NewapplicationService';
import { COMMON_ERROR_MSG } from '../../constants/constants';
import './ReportingCommon.scss';

const { Title } = Typography;
const { Option } = Select;

// Tested Metric options -- must match the backend's CK_SponserReportingRequirement_TestedMetric list.
const TESTED_METRIC_OPTIONS = ["IRR", "MOIC", "LTV", "DSCR", "Leverage", "N/A"];
const OPERATOR_OPTIONS = [">", ">=", "<", "<=", "="];
const PERCENTAGE_METRICS = ["IRR", "LTV"];

const getMetricUnit = (metric) => {
    if (PERCENTAGE_METRICS.includes(metric)) return "%";
    if (metric && metric !== "N/A") return "x";
    return "";
};

const formatRequiredValue = (record) => {
    if (!record || record.TestedMetric === "N/A" || record.ThresholdValue === null || record.ThresholdValue === undefined) {
        return "--";
    }
    return `${record.Operator} ${record.ThresholdValue}${record.MetricUnit || ""}`;
};

// Reporting Requirements are configured on this same Add/Edit Credit Facility form -- like Fund
// and Assign Internal Stakeholders -- rather than through an immediate-save API. Add/Edit/Delete
// here only mutate the `value` array (lifted into the parent form's state); the parent includes
// it in the AddOrUpdateSponser payload, and the backend creates/updates/deactivates the actual
// SponserReportingRequirement rows only once the Sponser itself is saved. This lets a brand-new
// (not yet saved) Credit Facility have covenants configured before it has an Id.
const CreditFacilityReportSection = ({ sponserId, loanProgrameTypeId, value, onChange, deletedIds, onDeletedIdsChange }) => {
    const [form] = Form.useForm();
    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRequirement, setEditingRequirement] = useState(null);
    const [selectedMetric, setSelectedMetric] = useState(null);
    const [flaggedCycleCount, setFlaggedCycleCount] = useState(0);

    const hasLoadedExisting = useRef(false);
    const localKeyRef = useRef(-1);

    const requirements = value || [];

    // Seed the locally-managed list once from whatever is already configured for an existing
    // (already saved) facility. After this, all edits happen purely in local state.
    useEffect(() => {
        if (!sponserId || hasLoadedExisting.current) {
            return;
        }
        hasLoadedExisting.current = true;
        setLoading(true);
        _ReportingService.getReportingRequirementsBySponserId(sponserId)
            .then((res) => {
                const existing = (res.data?.Obj || []).map((item) => ({ ...item, __localKey: item.Id }));
                if (existing.length) {
                    onChange(existing);
                }
            })
            .catch((error) => console.log(error))
            .finally(() => setLoading(false));
    }, [sponserId, onChange]);

    useEffect(() => {
        if (!sponserId) {
            setFlaggedCycleCount(0);
            return;
        }
        _ReportingService.getFacilityFlagStatusBySponserId(sponserId)
            .then((res) => setFlaggedCycleCount((res.data?.Obj || []).length))
            .catch((error) => console.log(error));
    }, [sponserId]);

    useEffect(() => {
        _NewapplicationService.getAllDropdownDataByName("documenttyepes")
            .then((res) => {
                if (res.data) {
                    setDocumentTypeOptions(res.data);
                }
            })
            .catch((error) => console.log(error));
    }, []);

    const openAddModal = () => {
        setEditingRequirement(null);
        setSelectedMetric(null);
        form.resetFields();
        setModalVisible(true);
    };

    const openEditModal = (record) => {
        setEditingRequirement(record);
        setSelectedMetric(record.TestedMetric);
        form.setFieldsValue({
            documentTypeId: record.DocumentTypeId,
            firstDueDate: record.FirstDueDate ? moment(record.FirstDueDate) : undefined,
            cadenceDays: record.CadenceDays,
            testedMetric: record.TestedMetric,
            operator: record.Operator,
            thresholdValue: record.ThresholdValue,
        });
        setModalVisible(true);
    };

    const handleDelete = (record) => {
        onChange(requirements.filter((r) => r.__localKey !== record.__localKey));
        if (record.Id > 0) {
            onDeletedIdsChange([...(deletedIds || []), record.Id]);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const isNA = values.testedMetric === "N/A";
            const docType = documentTypeOptions.find((d) => d.Id === values.documentTypeId);

            const updatedItem = {
                Id: editingRequirement ? editingRequirement.Id : 0,
                __localKey: editingRequirement ? editingRequirement.__localKey : localKeyRef.current--,
                DocumentTypeId: values.documentTypeId,
                DocumentTypeName: docType?.Name || "",
                FirstDueDate: values.firstDueDate.format("YYYY-MM-DD"),
                CadenceDays: values.cadenceDays,
                TestedMetric: values.testedMetric,
                Operator: isNA ? null : values.operator,
                ThresholdValue: isNA ? null : values.thresholdValue,
                MetricUnit: getMetricUnit(values.testedMetric),
            };

            if (editingRequirement) {
                onChange(requirements.map((r) => (r.__localKey === editingRequirement.__localKey ? updatedItem : r)));
            } else {
                onChange([...requirements, updatedItem]);
            }
            setModalVisible(false);
        } catch (error) {
            if (error?.errorFields) {
                // antd form validation error -- already shown inline, nothing else to do.
                return;
            }
            console.log(error);
            message.error(COMMON_ERROR_MSG);
        }
    };

    const columns = [
        { title: "Document", dataIndex: "DocumentTypeName", key: "DocumentTypeName" },
        { title: "First Due Date", dataIndex: "FirstDueDate", key: "FirstDueDate", render: (v) => v ? new Date(v).toLocaleDateString() : "--" },
        { title: "Cadence (Days)", dataIndex: "CadenceDays", key: "CadenceDays" },
        { title: "Tested Metric", dataIndex: "TestedMetric", key: "TestedMetric" },
        { title: "Required", key: "Required", render: (_, record) => formatRequiredValue(record) },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(record)}
                    />
                    <Popconfirm
                        title="Remove this reporting requirement?"
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (!loanProgrameTypeId) {
        return (
            <div className="card-section">
                <Title level={5} className="form-title">Credit Facility Report</Title>
                <p style={{ color: "rgba(244, 244, 245, 0.7)" }}>Select a Credit Facility Type above to configure Reporting Requirements.</p>
            </div>
        );
    }

    return (
        <div className="card-section credit-facility-report-section">
            <Title level={5} className="form-title">Credit Facility Report</Title>

            {flaggedCycleCount > 0 && (
                <Alert
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                    message={`This facility has ${flaggedCycleCount} unresolved reporting requirement failure${flaggedCycleCount > 1 ? "s" : ""}.`}
                />
            )}

            <div style={{ marginBottom: 16, textAlign: "right" }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                    Add Reporting Requirement
                </Button>
            </div>

            <Table
                rowKey="__localKey"
                columns={columns}
                dataSource={requirements}
                loading={loading}
                pagination={false}
                locale={{ emptyText: "No reporting requirements configured for this Credit Facility." }}
            />

            <Modal
                title={editingRequirement ? "Edit Reporting Requirement" : "Add Reporting Requirement"}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                destroyOnClose
                className="credit-facility-report-modal"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Document"
                        name="documentTypeId"
                        rules={[{ required: true, message: "Document is required" }]}
                    >
                        <Select placeholder="Select Document Type">
                            {documentTypeOptions.map((item) => (
                                <Option key={item.Id} value={item.Id}>{item.Name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="First Due Date"
                        name="firstDueDate"
                        rules={[{ required: true, message: "First Due Date is required" }]}
                    >
                        <DatePicker style={{ width: "100%" }} format="MM/DD/YYYY" />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Cadence (Days)&nbsp;
                                <Tooltip title="Each future cycle's due date is First Due Date plus a multiple of the cadence -- a late submission does not shift this schedule.">
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </span>
                        }
                        name="cadenceDays"
                        rules={[{ required: true, message: "Cadence (Days) is required" }]}
                    >
                        <InputNumber min={1} style={{ width: "100%" }} placeholder="e.g. 90" />
                    </Form.Item>

                    <Form.Item
                        label="Tested Metric"
                        name="testedMetric"
                        rules={[{ required: true, message: "Tested Metric is required" }]}
                    >
                        <Select
                            placeholder="Select Tested Metric"
                            onChange={(value) => {
                                setSelectedMetric(value);
                                if (value === "N/A") {
                                    form.setFieldsValue({ operator: undefined, thresholdValue: undefined });
                                }
                            }}
                        >
                            {TESTED_METRIC_OPTIONS.map((metric) => (
                                <Option key={metric} value={metric}>{metric}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedMetric && selectedMetric !== "N/A" && (
                        <>
                            <Form.Item
                                label="Operator"
                                name="operator"
                                rules={[{ required: true, message: "Operator is required" }]}
                            >
                                <Select placeholder="Select Operator">
                                    {OPERATOR_OPTIONS.map((op) => (
                                        <Option key={op} value={op}>{op}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={`Threshold Value (${getMetricUnit(selectedMetric)})`}
                                name="thresholdValue"
                                rules={[{ required: true, message: "Threshold Value is required" }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={PERCENTAGE_METRICS.includes(selectedMetric) ? 100 : undefined}
                                    step={0.1}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default CreditFacilityReportSection;
